import { useEffect, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import { Markdown } from '@tiptap/markdown';
import styles from './RichText.module.css';

interface RichTextProps {
  /** Initial markdown value. */
  value: string;
  /** Fired on blur and on unmount with the current markdown (if changed). */
  onSave: (markdown: string) => void;
  placeholder?: string;
}

/** WYSIWYG editor that stores/emits standard Markdown. Saves on blur and on
 *  unmount so edits aren't lost when the drawer closes. */
export function RichText({ value, onSave, placeholder }: RichTextProps) {
  const latest = useRef(value);
  const initial = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? 'Write a description…' }),
    ],
    content: value || '',
    contentType: 'markdown',
    onUpdate: ({ editor }) => {
      latest.current = editor.getMarkdown();
    },
    onBlur: () => flush(),
  });

  const flush = () => {
    if (latest.current !== initial.current) {
      initial.current = latest.current;
      onSave(latest.current);
    }
  };

  // Flush pending edits when the component unmounts (e.g. drawer closes).
  useEffect(() => {
    return () => {
      if (latest.current !== initial.current) onSave(latest.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!editor) return null;

  const btn = (active: boolean) =>
    `${styles.tool} ${active ? styles.toolActive : ''}`;

  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={btn(editor.isActive('bold'))}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          className={btn(editor.isActive('italic'))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
          style={{ fontStyle: 'italic' }}
        >
          i
        </button>
        <button
          type="button"
          className={btn(editor.isActive('strike'))}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough"
          style={{ textDecoration: 'line-through' }}
        >
          S
        </button>
        <span className={styles.sep} />
        <button
          type="button"
          className={btn(editor.isActive('heading', { level: 2 }))}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          className={btn(editor.isActive('heading', { level: 3 }))}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Subheading"
        >
          H3
        </button>
        <span className={styles.sep} />
        <button
          type="button"
          className={btn(editor.isActive('bulletList'))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet list"
        >
          •
        </button>
        <button
          type="button"
          className={btn(editor.isActive('orderedList'))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          1.
        </button>
        <button
          type="button"
          className={btn(editor.isActive('blockquote'))}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Quote"
        >
          &ldquo;
        </button>
        <button
          type="button"
          className={btn(editor.isActive('code'))}
          onClick={() => editor.chain().focus().toggleCode().run()}
          title="Inline code"
        >
          {'</>'}
        </button>
        <span className={styles.sep} />
        <button
          type="button"
          className={btn(editor.isActive('link'))}
          onClick={setLink}
          title="Link"
        >
          Link
        </button>
      </div>
      <EditorContent editor={editor} className={styles.content} />
    </div>
  );
}
